import { useState, useCallback, type ReactElement } from 'react'
import { ScanInstructionsScreen } from './ScanInstructionsScreen.js'
import { useStep } from '../state/useStep.js'
import { useDemoMode } from '../state/useDemoMode.js'
import { useDemoStore, type Material } from '../state/store.js'
import { LiveCameraScan } from '../live/LiveCameraScan.js'
import { DemoVideoScan } from '../live/DemoVideoScan.js'
import { SCAN_CONFIGS } from '../live/scans.js'
import { useGrantLifetime, type CameraGrant } from '../live/useRequestCameraGrant.js'

const CONFIG = SCAN_CONFIGS.countertop

export const ScanCountertopScreen = (): ReactElement => {
  const { goto } = useStep()
  const { dispatch } = useDemoStore()
  const [phase, setPhase] = useState<'instructions' | 'scanning'>('instructions')
  const [grant, setGrant] = useState<CameraGrant | null>(null)
  const demo = useDemoMode()

  useGrantLifetime(grant)

  const onComplete = useCallback((findings: unknown) => {
    dispatch({
      type: 'COUNTERTOP_DETECTED',
      findings: findings as { material: Material; overhangInches: number; materialConfidence: number },
    })
    goto('countertop-results')
  }, [dispatch, goto])

  const onSkipOrAbort = useCallback(() => {
    dispatch({
      type: 'COUNTERTOP_DETECTED',
      findings: { material: 'Granite', overhangInches: 1.25, materialConfidence: 0 },
    })
    goto('countertop-results')
  }, [dispatch, goto])

  const onProceed = useCallback((g: CameraGrant | null) => {
    dispatch({ type: 'CLEAR_LIVE_PATCH', scan: 'countertop' })
    if (g) setGrant(g)
    setPhase('scanning')
  }, [dispatch])

  if (phase === 'instructions') {
    return (
      <ScanInstructionsScreen
        title="Scan instructions"
        backTo="model-lookup"
        headline="Now let's check your countertop"
        body="Point your camera at the countertop above your dishwasher. We'll check the material and measure the overhang."
        tip="Make sure the countertop edge is clearly visible."
        ctaLabel="Open Camera"
        hero={<div className="border-2 border-dashed border-primary rounded-xl w-28 h-24" />}
        onProceed={onProceed}
        onSkip={onSkipOrAbort}
      />
    )
  }

  if (demo) {
    return (
      <DemoVideoScan
        scanConfig={CONFIG}
        onComplete={onComplete}
        onAbort={onSkipOrAbort}
      />
    )
  }

  if (!grant) return null as unknown as ReactElement
  return <LiveCameraScan scanConfig={CONFIG} grant={grant} onComplete={onComplete} onAbort={onSkipOrAbort} />
}
